/**
 * AlbMove CSMS - WebSocket Context for Real-time Events
 * Provides real-time updates for charger status, commands, and notifications
 * 
 * PRODUCTION CONFIG:
 * - WebSocket URL: wss://api.albmove.al/api/ws/v1/...
 * - Reconnect: Exponential backoff (1s, 2s, 5s, 10s, 30s max)
 * - Heartbeat: Ping every 30s
 */
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Reconnect configuration
const RECONNECT_CONFIG = {
  INITIAL_DELAY: 1000,      // 1 second
  MAX_DELAY: 30000,         // 30 seconds max
  MAX_ATTEMPTS: 10,         // Max reconnect attempts before giving up
  PING_INTERVAL: 30000,     // 30 seconds heartbeat
};

// Event types that we handle
export const EventTypes = {
  // Charger status events
  CHARGER_STATUS_CHANGED: 'charger.status.changed',
  CHARGER_AVAILABILITY_CHANGED: 'charger.availability.changed',
  CHARGER_CONNECTION_ONLINE: 'charger.connection.online',
  CHARGER_CONNECTION_OFFLINE: 'charger.connection.offline',
  
  // Connector status events
  CONNECTOR_STATUS_CHANGED: 'connector.status.changed',
  CONNECTOR_POWER_UPDATED: 'connector.power.updated',
  
  // Command events
  COMMAND_CREATED: 'command.created',
  COMMAND_SENT: 'command.sent',
  COMMAND_ACCEPTED: 'command.accepted',
  COMMAND_REJECTED: 'command.rejected',
  COMMAND_TIMEOUT: 'command.timeout',
  
  // Session events
  SESSION_STARTED: 'session.started',
  SESSION_UPDATED: 'session.updated',
  SESSION_COMPLETED: 'session.completed',
  
  // Notification events
  NOTIFICATION: 'notification',
  
  // Initial state
  INITIAL_STATE: 'initial_state'
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { token, isAuthenticated, user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connected' | 'connecting' | 'disconnected' | 'error'
  const [lastEvent, setLastEvent] = useState(null);
  const [chargerStates, setChargerStates] = useState({});
  const [pendingCommands, setPendingCommands] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const eventListenersRef = useRef({});

  // Get WebSocket URL based on user role - uses new v1 endpoints
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert HTTP(S) to WS(S)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    // Remove /api if present and add it back with ws path
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    
    // Use role-based endpoint for better filtering
    const role = user?.role?.toUpperCase();
    if (role === 'ADMIN' || role === 'SUPER_ADMIN') {
      return `${wsProtocol}://${wsHost}/api/ws/v1/admin?token=${token}`;
    } else if (role === 'MODERATOR') {
      return `${wsProtocol}://${wsHost}/api/ws/v1/moderator?token=${token}`;
    }
    
    // Fallback to legacy dashboard endpoint
    return `${wsProtocol}://${wsHost}/api/ws/dashboard?token=${token}`;
  }, [token, user?.role]);

  // Subscribe to events
  const subscribe = useCallback((eventType, callback) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = [];
    }
    eventListenersRef.current[eventType].push(callback);
    
    // Return unsubscribe function
    return () => {
      eventListenersRef.current[eventType] = eventListenersRef.current[eventType].filter(
        cb => cb !== callback
      );
    };
  }, []);

  // Emit to local listeners
  const emitToListeners = useCallback((eventType, data) => {
    const listeners = eventListenersRef.current[eventType] || [];
    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error('Error in event listener:', e);
      }
    });
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      
      // Handle both old format (type/data) and new format (event/data from ws_manager)
      const eventType = message.type || message.event;
      const data = message.data || {};
      const timestamp = message.timestamp || message.ts;
      
      setLastEvent({ type: eventType, data, timestamp });
      
      // Update local state based on event type
      switch (eventType) {
        // New charger connection notification
        case 'charger.new_connection':
          emitToListeners('charger.new_connection', data);
          break;
          
        // New v1 events from WebSocket Manager
        case 'charger.created':
        case 'charger.updated':
        case 'charger.deleted':
        case 'charger.status':
        case 'site.created':
        case 'site.updated':
        case 'site.deleted':
        case 'session.started':
        case 'session.updated':
        case 'session.completed':
        case 'client.registered':
        case 'stats.refresh':
        case 'connector.status':
        case 'connector.status.updated':
          // Emit to listeners AND update chargerStates for real-time connector updates
          {
            const connData = data;
            setChargerStates(prev => {
              // Try matching by charger_id (OCPP ID)
              let key = Object.keys(prev).find(k => 
                prev[k]?.charger_id === connData.charger_id || k === connData.charger_id
              );
              if (!key) {
                emitToListeners(eventType, data);
                return prev;
              }
              const chargerState = prev[key];
              const updatedConnectors = (chargerState.connectors || []).map(conn => {
                if (conn.connector_id === connData.connector_id) {
                  return {
                    ...conn,
                    status: connData.status,
                    current_power_kw: connData.current_power_kw ?? conn.current_power_kw ?? 0,
                    soc_percent: connData.soc_percent ?? conn.soc_percent ?? null
                  };
                }
                return conn;
              });
              return {
                ...prev,
                [key]: {
                  ...chargerState,
                  status: connData.charger_status || chargerState.status,
                  connectors: updatedConnectors
                }
              };
            });
          }
          emitToListeners(eventType, data);
          break;
          
        case 'connection.established':
          console.log('WebSocket v1 connection established:', data);
          break;
          
        case 'ping':
          // Respond to ping with pong
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'ping' }));
          }
          break;
          
        case EventTypes.INITIAL_STATE:
          // Initialize charger states from server (now includes connectors)
          const chargerMap = {};
          data.chargers?.forEach(c => {
            chargerMap[c.id] = {
              charger_id: c.charger_id,
              status: c.status,
              is_online: c.is_online,
              last_seen: c.last_seen,
              connectors: c.connectors || []
            };
          });
          setChargerStates(chargerMap);
          break;
          
        case EventTypes.CHARGER_STATUS_CHANGED:
        case EventTypes.CHARGER_AVAILABILITY_CHANGED:
          // Update charger state
          setChargerStates(prev => {
            // Find charger key by OCPP ID or direct key match
            let stateKey = Object.keys(prev).find(k =>
              prev[k]?.charger_id === data.charger_id || k === data.charger_id
            );
            if (!stateKey) return prev;
            return {
              ...prev,
              [stateKey]: {
                ...prev[stateKey],
                charger_name: data.charger_name,
                status: data.new_status,
                is_online: data.is_online,
                old_status: data.old_status
              }
            };
          });
          
          // Show toast notification
          const statusMsg = data.new_status === 'Available' 
            ? `✅ ${data.charger_name} u aktivizua`
            : data.new_status === 'Unavailable'
              ? `⛔ ${data.charger_name} u çaktivizua`
              : `🔄 ${data.charger_name}: ${data.new_status}`;
          toast.info(statusMsg, { duration: 4000 });
          break;
          
        case EventTypes.CHARGER_CONNECTION_ONLINE:
          setChargerStates(prev => {
            let stateKey = Object.keys(prev).find(k =>
              prev[k]?.charger_id === data.charger_id || k === data.charger_id
            );
            if (!stateKey) stateKey = data.charger_id;
            return {
              ...prev,
              [stateKey]: {
                ...prev[stateKey],
                is_online: true,
                status: prev[stateKey]?.status || 'Available'
              }
            };
          });
          toast.success(`🟢 ${data.charger_name} është Online`, { duration: 4000 });
          break;
          
        case EventTypes.CHARGER_CONNECTION_OFFLINE:
          setChargerStates(prev => {
            let stateKey = Object.keys(prev).find(k =>
              prev[k]?.charger_id === data.charger_id || k === data.charger_id
            );
            if (!stateKey) stateKey = data.charger_id;
            return {
              ...prev,
              [stateKey]: {
                ...prev[stateKey],
                is_online: false,
                status: 'Offline'
              }
            };
          });
          toast.warning(`🔴 ${data.charger_name} shkoi Offline`, { duration: 4000 });
          break;
        
        case EventTypes.CONNECTOR_STATUS_CHANGED:
          // Update connector status within charger state
          setChargerStates(prev => {
            let stateKey = Object.keys(prev).find(k =>
              prev[k]?.charger_id === data.charger_id || k === data.charger_id
            );
            if (!stateKey) return prev;
            const chargerState = prev[stateKey];
            
            const updatedConnectors = (chargerState.connectors || []).map(conn => {
              if (conn.connector_id === data.connector_id) {
                return {
                  ...conn,
                  status: data.new_status,
                  current_power_kw: data.current_power_kw || 0
                };
              }
              return conn;
            });
            
            return {
              ...prev,
              [stateKey]: {
                ...chargerState,
                connectors: updatedConnectors
              }
            };
          });
          
          // Show connector status toast
          const connLabel = String.fromCharCode(64 + data.connector_id);
          const connStatusMsg = data.new_status === 'Charging'
            ? `⚡ ${data.charger_name} Porta ${connLabel}: Po ngarkon ${data.current_power_kw?.toFixed(1) || 0} kW`
            : data.new_status === 'Available'
              ? `✅ ${data.charger_name} Porta ${connLabel}: E lirë`
              : `🔄 ${data.charger_name} Porta ${connLabel}: ${data.new_status}`;
          toast.info(connStatusMsg, { duration: 3000 });
          break;
        
        case EventTypes.CONNECTOR_POWER_UPDATED:
          // Update connector power without toast (frequent updates)
          setChargerStates(prev => {
            const chargerState = prev[data.charger_id];
            if (!chargerState) return prev;
            
            const updatedConnectors = (chargerState.connectors || []).map(conn => {
              if (conn.connector_id === data.connector_id) {
                return {
                  ...conn,
                  current_power_kw: data.current_power_kw,
                  status: data.status || conn.status
                };
              }
              return conn;
            });
            
            return {
              ...prev,
              [data.charger_id]: {
                ...chargerState,
                connectors: updatedConnectors
              }
            };
          });
          break;
          
        case EventTypes.COMMAND_CREATED:
          // Add to pending commands
          setPendingCommands(prev => ({
            ...prev,
            [data.command_id]: {
              ...data,
              status: 'created'
            }
          }));
          toast.info(`⏳ ${data.command_type} në proces për ${data.charger_name}...`, { 
            id: data.command_id,
            duration: 10000 
          });
          break;
          
        case EventTypes.COMMAND_SENT:
          // Update pending command status
          setPendingCommands(prev => ({
            ...prev,
            [data.command_id]: {
              ...prev[data.command_id],
              status: 'sent'
            }
          }));
          break;
          
        case EventTypes.COMMAND_ACCEPTED:
          // Command was accepted
          setPendingCommands(prev => {
            const { [data.command_id]: removed, ...rest } = prev;
            return rest;
          });
          toast.success(`✅ ${data.command_type} u pranua për ${data.charger_name}`, { 
            id: data.command_id,
            duration: 4000 
          });
          break;
          
        case EventTypes.COMMAND_REJECTED:
          // Command was rejected
          setPendingCommands(prev => {
            const { [data.command_id]: removed, ...rest } = prev;
            return rest;
          });
          toast.error(`❌ ${data.command_type} u refuzua: ${data.message || 'Gabim'}`, { 
            id: data.command_id,
            duration: 5000 
          });
          break;
          
        case EventTypes.COMMAND_TIMEOUT:
          // Command timed out
          setPendingCommands(prev => {
            const { [data.command_id]: removed, ...rest } = prev;
            return rest;
          });
          toast.error(`⏰ ${data.command_type} dështoi (timeout) për ${data.charger_name}`, { 
            id: data.command_id,
            duration: 5000 
          });
          break;
          
        case EventTypes.NOTIFICATION:
          // Handle notification event
          const severity = data.severity || 'INFO';
          const toastFn = severity === 'CRITICAL' ? toast.error 
            : severity === 'WARNING' ? toast.warning 
            : toast.info;
          toastFn(`${data.title}: ${data.message}`, { duration: 6000 });
          break;
        
        case EventTypes.SESSION_STARTED:
          // Session started - notify reports to refresh
          toast.info(`⚡ Sesion i ri: ${data.charger_name}`, { duration: 3000 });
          break;
        
        case EventTypes.SESSION_COMPLETED:
          // Session completed - notify reports to refresh
          const energyDisplay = data.energy_kwh?.toFixed(2) || '0';
          const costDisplay = data.cost_lek?.toFixed(0) || '0';
          toast.success(`✅ Sesion i përfunduar: ${energyDisplay} kWh, ${costDisplay} LEK`, { duration: 4000 });
          break;
        
        case 'session.progress':
          // Live session progress - update connector power/SoC in chargerStates
          if (data.charger_id && data.connector_id) {
            setChargerStates(prev => {
              let key = Object.keys(prev).find(k => 
                prev[k]?.charger_id === data.charger_id || k === data.charger_id
              );
              if (!key) return prev;
              const chargerState = prev[key];
              const updatedConnectors = (chargerState.connectors || []).map(conn => {
                if (conn.connector_id === data.connector_id) {
                  return {
                    ...conn,
                    current_power_kw: data.power_kw ?? data.current_power_kw ?? conn.current_power_kw,
                    soc_percent: data.soc_percent ?? conn.soc_percent
                  };
                }
                return conn;
              });
              return {
                ...prev,
                [key]: { ...chargerState, connectors: updatedConnectors }
              };
            });
          }
          break;
          
        default:
          // Unknown event type - just emit to listeners
          break;
      }
      
      // Emit to all subscribers
      emitToListeners(eventType, data);
      emitToListeners('*', { type: eventType, data, timestamp }); // Wildcard listeners
      
    } catch (e) {
      console.error('Error parsing WebSocket message:', e);
    }
  }, [emitToListeners]);

  // Calculate reconnect delay with exponential backoff
  const getReconnectDelay = useCallback(() => {
    const attempts = reconnectAttemptsRef.current;
    const delay = Math.min(
      RECONNECT_CONFIG.INITIAL_DELAY * Math.pow(2, attempts),
      RECONNECT_CONFIG.MAX_DELAY
    );
    return delay;
  }, []);

  // Start ping interval for heartbeat
  const startPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, RECONNECT_CONFIG.PING_INTERVAL);
  }, []);

  // Stop ping interval
  const stopPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!isAuthenticated || !token) {
      return;
    }
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    setConnectionStatus('connecting');
    
    try {
      const wsUrl = getWsUrl();
      console.log('[WS] Connecting to:', wsUrl.replace(token, '***'));
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('[WS] Connected successfully');
        setConnected(true);
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0; // Reset attempts on successful connection
        
        // Start heartbeat ping
        startPing();
        
        // Clear reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setConnected(false);
        setConnectionStatus('disconnected');
        stopPing();
        
        // Auto-reconnect with exponential backoff
        if (isAuthenticated && !reconnectTimeoutRef.current) {
          const delay = getReconnectDelay();
          const attempts = reconnectAttemptsRef.current;
          
          if (attempts < RECONNECT_CONFIG.MAX_ATTEMPTS) {
            console.log(`[WS] Reconnecting in ${delay}ms (attempt ${attempts + 1}/${RECONNECT_CONFIG.MAX_ATTEMPTS})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null;
              reconnectAttemptsRef.current++;
              connect();
            }, delay);
          } else {
            console.warn('[WS] Max reconnect attempts reached. Manual refresh required.');
            setConnectionStatus('error');
          }
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('[WS] Error:', error);
        setConnectionStatus('error');
      };
      
      wsRef.current.onmessage = handleMessage;
      
    } catch (error) {
      console.error('[WS] Failed to connect:', error);
      setConnectionStatus('error');
    }
  }, [isAuthenticated, token, getWsUrl, handleMessage, getReconnectDelay, startPing, stopPing]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    stopPing();
    reconnectAttemptsRef.current = 0;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
    setConnectionStatus('disconnected');
  }, [stopPing]);

  // Manual reconnect (reset attempts)
  const manualReconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(() => connect(), 500);
  }, [disconnect, connect]);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Get charger state by ID
  const getChargerState = useCallback((chargerId) => {
    return chargerStates[chargerId] || null;
  }, [chargerStates]);

  // Check if command is pending for charger
  const isCommandPending = useCallback((chargerId) => {
    return Object.values(pendingCommands).some(cmd => cmd.charger_id === chargerId);
  }, [pendingCommands]);

  // Connect on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [isAuthenticated, token, connect, disconnect]);

  // Cleanup ping interval is now handled in connect/disconnect

  const value = {
    connected,
    connectionStatus, // 'connected' | 'connecting' | 'disconnected' | 'error'
    lastEvent,
    chargerStates,
    pendingCommands,
    subscribe,
    getChargerState,
    isCommandPending,
    connect,
    disconnect,
    manualReconnect // For manual reconnection button
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
